import React, { useContext, useEffect, useState } from 'react';
import { EventContext } from '../context/EventContext';
import { getEventByCode } from '../services/eventService';
import { Event } from '../types/Event';
import { CustomRegistrationQuestion, EventSelectedRegistrationQuestion } from '../types/RegistrationQuestion';
import { getEventSelectedRegistrationQuestions } from '../services/eventSelectedRegistrationQuestionService';
import { RegistrationQuestionComponent } from './RegistrationQuestionComponent';
import logo from '../assets/logo.png';
import { UserRoleContext } from '../context/UserRoleContext';
import { useAuth } from '../context/AuthContext';
import { Answer, CreateRegistrant, CustomQuestionAnswer, EnrichedRegistrant, FileAnswer, InstrumentAnswer, TextAnswer } from '../types/Registrant';
import { createRegistrant, fetchRegistrantById, updateRegistrant } from '../services/registrantService';
import { uploadAndRegisterFile } from '../services/registrantFileService';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { Organization } from '../types/Organization';
import { getOrganizationByCode } from '../services/organizationService';
import { getCustomRegistrationQuestionsForEvent } from '../services/customRegistrationQuestionService';
import { RegistrationCustomQuestionComponent } from './RegistrationCustomQuestionComponent';
import { Instrument } from '../types/Instrument';
import { ROUTES } from '../constants/routes';
import { formatPhoneNumber } from '../services/formatter.service';



export const EditRegistrantComponent: React.FC = () => {
  const { currentEvent, setCurrentEvent } = useContext(EventContext); // Event context
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const { userRole } = useContext(UserRoleContext);
  const [eventCode, setEventCode] = useState<string>(''); // State for the event code
  const [organizationCode, setOrganizationCode] = useState<string>(''); // State for the event code
  const [searchedEvent, setSearchedEvent] = useState<Event | null>(null); // State for the returned event
  const [searchedOrganization, setSearchedOrganization] = useState<Organization | null>(null); // State for the returned event
  const [showEventSearchComponents, setShowEventSearchComponents] = useState<boolean>(!currentEvent); // Boolean for showing search components
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // State for error message
  const [eventSelectedQuestions, setEventSelectedQuestions] = useState<EventSelectedRegistrationQuestion[]>([]);
  const [customRegistrationQuestions, setCustomRegistrationQuestions] = useState<CustomRegistrationQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string | number | File>>({});
  const [customAnswers, setCustomAnswers] = useState<Record<string, string | number>>({});
  const [registrantIsUser, setRegistrantIsUser] = useState<boolean>(false);
  const [associateOrganization, setAssociateOrganization] = useState<boolean>(true);
  const [willCreateAccount, setWillCreateAccount] = useState<boolean>(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrant, setRegistrant] = useState<EnrichedRegistrant>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  // const [TandC, setTandC] = useState<boolean>(false);

  const { registrant_id } = useParams<{ registrant_id: string }>();

  const { isAuthenticated, user } = useAuth();
  const [regDetailAnswers, setRegDetailAnswers] = useState<Record<string, string | number>>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });


  const handleAnswer = (id: string, value: string | number | File) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [id]: value,
    }));
  };

  const handleCustomAnswer = (id: string, value: string | number) => {
    setCustomAnswers((prevAnswers) => ({
      ...prevAnswers,
      [id]: value,
    }));
  };


  const loadRegistrant = async () => {
    try {
    if (registrant_id) {
      const reg = await fetchRegistrantById(registrant_id);
      if (reg?.registrant_id) {
        setRegistrant(reg);
        setRegDetailAnswers({
          firstName: reg.first_name,
          lastName: reg.last_name,
          email: reg.email,
          phoneNumber: reg.phone_number
        });
        //console.log(reg.answers);

        const presetAnswers: Record<string, string | number | File> = {};
        reg.answers.forEach((ans) => {
          if (ans.type == 'instrument') {
            const a = ans as InstrumentAnswer;
            presetAnswers[ans.event_selected_registration_question_id] = a.answer.instrument_id;
          } else if (ans.type != 'file') {
            const a = ans as TextAnswer;
            presetAnswers[ans.event_selected_registration_question_id] = a.answer?.toString() ?? '';
          } else if (ans.type == 'file') {
            const a = ans as FileAnswer;
            presetAnswers[ans.event_selected_registration_question_id] = a.answer?.file_name.toString() ?? '';
          }
        });
        setAnswers(presetAnswers);

        // Custom answers
        const customAns: Record<string, string | number> = {};
        reg.custom_answers.forEach((cust) => {
          customAns[cust.custom_registration_question_id] = cust.answer_text;
        });
        setCustomAnswers(customAns);

      } else {
        addToast('You do not have permission to view this registrant.', true);
        navigate(ROUTES.HOME);
      }
    } else {
      addToast('No registrant id, please navigate through registrant list.', true);
      navigate(ROUTES.HOME);
    }
  } catch (error) {
    addToast('I see what you\'re doing, and I punish you');
    navigate(ROUTES.HOME);
  };
  };


  // Handle the answer submission or change
  const handleRegDetailsAnswer = (id: string, value: string | number) => {
    setRegDetailAnswers((prevAnswers) => ({
      ...prevAnswers,
      [id]: value,
    }));
  };

  // Handle changes for first name, last name, and email
  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegDetailAnswers((prevAnswers) => ({
      ...prevAnswers,
      [name]: value,
    }));
  };

  // Fetch events when the component mounts
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        if (currentEvent) {
          //update event
          const questions = await getEventSelectedRegistrationQuestions(currentEvent?.event_id);//(currentEvent?.event_id); // Fetch the events
          setEventSelectedQuestions(questions); // Update the state with fetched events
          const customQuestions = await getCustomRegistrationQuestionsForEvent(currentEvent?.event_id);
          setCustomRegistrationQuestions(customQuestions);
          loadRegistrant();
        } else {
          addToast('No current event, please select your event.', true);
          navigate(ROUTES.HOME);
          return;
        }
      } catch (error) {
        console.error("Error fetching event:", error); // Log any errors
      }
    };
    fetchEvents();
  }, []); // Empty dependency array ensures this runs once on mount


  const submitUpdateRegistrant = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    try {
      if (!currentEvent) {
        setErrorMessage("No event selected.");
        return;
      }

    // Basic validation
      if (!regDetailAnswers.firstName || !regDetailAnswers.lastName || !regDetailAnswers.phoneNumber || !regDetailAnswers.email) {
        setErrorMessage("Please fill in all required fields (First Name, Last Name, Phone Number, Email).");
        return;
      }

     // Build the registration answers array without the files;
      const fileQuestionIds = new Set(
        eventSelectedQuestions
          .filter(q => q.question_type === 'file')
          .map(q => String(q.event_selected_registration_question_id))
      );


      const registrationAnswers: Answer[] = Object.entries(answers)
        .filter(([question_id]) => !fileQuestionIds.has(question_id))
        .map(([question_id, answer_value]) => ({
          preset_registration_question_id: question_id,
          answer: String(answer_value),
        }));

      const fileUploads = Object.entries(answers)
        .filter(([question_id, value]) =>
          fileQuestionIds.has(question_id) && value instanceof File
        )
        .map(([question_id, file]) => ({
          preset_registration_question_id: question_id,
          file: file as File,
        }));


      const answersArray: CustomQuestionAnswer[] = Object.entries(customAnswers).map(
        ([custom_registration_question_id, answer_text]) => ({ question_text: '', custom_registration_question_id, answer_text })
      );

      const formattedPhoneNumber = formatPhoneNumber(String(regDetailAnswers.phoneNumber));
      if (!formattedPhoneNumber) {
        addToast("Phone number is not in proper format", true);
        return;
      }

      const registrantToUpdate: CreateRegistrant = {
        event_id: currentEvent.event_id,
        first_name: String(regDetailAnswers.firstName),
        last_name: String(regDetailAnswers.lastName),
        email: String(regDetailAnswers.email),
        phone_number: formattedPhoneNumber, // optional
        answers: registrationAnswers,
        custom_answers: answersArray,
        create_account: willCreateAccount,
        registrant_is_user: registrantIsUser, // you might need to include this if your API expects it
        organization_id: registrant?.organization_id ?? ''
      };
      if (registrant && registrant_id) {
        const success = await updateRegistrant(registrant.registrant_id, registrantToUpdate);

        for (const { preset_registration_question_id, file } of fileUploads) {
          await uploadAndRegisterFile(registrant_id, file, preset_registration_question_id);
        }

        if (success) {
          addToast('Registrant updated successfully');
          navigate(ROUTES.REGISTRANTS_LIST);
        } else {
          addToast('Error updating registrant', true);
        }
      } else {
        console.error('No registrant to update');
        addToast('Error, no registrant to update', true);
      }
      
    } catch (error) {
      console.error(error);
      setErrorMessage("An unexpected error occurred during registration.");
    }
  };

  const cancelSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  }

  return (
    <div style={{ alignContent: 'center', justifyContent: 'center', display: 'flex', width: '100%' }}>
      <form style={{ marginBottom: '20px' }} onSubmit={cancelSubmit}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={logo} alt="Cyne Up" style={{ maxWidth: '50%' }} />
        </div>
        <div style={{ display: 'flex', marginRight: '5px', alignItems: 'center' }}>
          <h2 style={{ marginRight: '20px' }}>
            {currentEvent ? `${currentEvent?.event_name} - (${currentEvent?.event_code})` : 'Select an Event'}
          </h2>
        </div>
        {/* Organization Search */}

        {(currentEvent?.require_organization == true && (userRole?.organization_id == "" || userRole?.organization_id == null)) && (
          <div>
            <label><strong>Organization</strong><span style={{ color: 'red' }}> *</span></label>
            <label>{registrant?.organization_name ? registrant.organization_name : 'No organization affiliation.'}</label>
          </div>
        )}

        {/* Rest of form */}
        {(currentEvent) && <div>

          {/* Input for First Name */}
          <div style={{ marginBottom: '20px', width: '100%', maxWidth: '500px' }}>
            <label>
              <strong>First Name<span style={{ color: 'red' }}> *</span></strong>
              <input
                type="text"
                name="firstName"
                value={regDetailAnswers.firstName}
                // value={typeof answers.firstName === 'string' ? answers.firstName : ''}
                onChange={handleTextInputChange}
                required
              />
            </label>
          </div>

          {/* Input for Last Name */}
          <div style={{ marginBottom: '20px', width: '100%', maxWidth: '500px' }}>
            <label>
              <strong>Last Name<span style={{ color: 'red' }}> *</span></strong>
              <input
                type="text"
                name="lastName"
                value={regDetailAnswers.lastName}
                // value={typeof answers.lastName === 'string' ? answers.lastName : ''}
                onChange={handleTextInputChange}
                required
              />
            </label>
          </div>

          <div style={{ marginBottom: '20px', width: '100%', maxWidth: '500px' }}>
            <label><strong>Mobile Phone Number <span style={{ color: 'gray' }}> (Used for event messaging and alerts)</span><span style={{ color: 'red' }}> *</span></strong>
              <input
                type="text"
                name="phoneNumber"
                value={regDetailAnswers.phoneNumber}
                onChange={handleTextInputChange}
                required
                minLength={10}
                maxLength={12}
                placeholder='2345678901'
              />
            </label>
          </div>

          {/* Input for Email */}
          <div style={{ marginBottom: '20px', width: '100%', maxWidth: '500px' }}>
            <label>
              <strong>Email <span style={{ color: 'gray' }}> (If not used, registrant cannot make a Cyne Up account)</span><span style={{ color: 'red' }}> *</span></strong>
              <input
                type="email"
                name="email"
                value={regDetailAnswers.email}
                // value={typeof answers.phoneNumber === 'string' ? answers.phoneNumber : ''}
                onChange={handleTextInputChange}
                required
                minLength={5}
                maxLength={255}
              />
            </label>
          </div>


          {currentEvent && eventSelectedQuestions.length > 0 && eventSelectedQuestions.map((question) => (
            <RegistrationQuestionComponent
              key={question.event_selected_registration_question_id}
              question={question}
              onAnswer={handleAnswer}
              answer={answers[question.event_selected_registration_question_id]}
              event_id={currentEvent.event_id}
            // Provide the pre-filled answer if any
            />
          ))}

          {currentEvent && customRegistrationQuestions.length > 0 && customRegistrationQuestions.map((question) => (
            <RegistrationCustomQuestionComponent
              key={question.custom_registration_question_id}
              question={question}
              onCustomAnswer={handleCustomAnswer}
              customAnswer={customAnswers[question.custom_registration_question_id]}
              event_id={currentEvent.event_id}
            // Provide the pre-filled answer if any
            />
          ))}

          <p><strong>Cyne Up values your privacy. Cyne Up does not sell, rent, or share your personal information with 3rd parties.</strong></p>

          <br />
          <div className='buttonsRow'>
            <button className='cancelButton' onClick={() => navigate(ROUTES.REGISTRANTS_LIST)}>Cancel</button>
            <button type="button" disabled={!currentEvent || !registrant?.registrant_id} onClick={submitUpdateRegistrant}>Update</button>
          </div>

        </div>}
      </form>
    </div>
  );
};
